import { Component, OnInit, OnDestroy, ViewChild, ElementRef, inject, ChangeDetectionStrategy, signal } from '@angular/core';

import { FormsModule } from '@angular/forms';
import {
  MatDialogRef,
  MAT_DIALOG_DATA,
  MatDialogModule,
} from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTabsModule } from '@angular/material/tabs';
import { SnapshotService } from '../../shared/services/snapshot/snapshot.service';
import { MarkdownService } from '../../shared/services/markdown/markdown.service';
import { ToastrService } from 'ngx-toastr';
import { AutoUnsubscribe } from '../../shared/decorators/auto-unsubscribe.decorator';
import { Subscription } from 'rxjs';
import { Snapshot } from '../../shared/interfaces/snapshot.interface';

export interface EditSnapshotModalData {
  snapshot: Snapshot;
}

export interface EditSnapshotModalResult {
  action: 'save' | 'cancel';
  snapshotName?: string;
  description?: string;
}

@AutoUnsubscribe()
@Component({
  selector: 'app-edit-snapshot-modal',
  standalone: true,
  imports: [
    FormsModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatTabsModule,
  ],
  templateUrl: './edit-snapshot-modal.component.html',
  styleUrls: ['./edit-snapshot-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EditSnapshotModalComponent implements OnInit, OnDestroy {
  private dialogRef = inject<MatDialogRef<EditSnapshotModalComponent>>(MatDialogRef);
  data = inject<EditSnapshotModalData>(MAT_DIALOG_DATA);
  private snapshotService = inject(SnapshotService);
  private markdownService = inject(MarkdownService);
  private toastrService = inject(ToastrService);

  @ViewChild('nameInput') nameInput?: ElementRef<HTMLInputElement>;

  snapshotName: string = '';
  description: string = '';
  isSaving = signal(false);

  // Markdown support properties
  renderedMarkdown = signal('');
  isPreviewMode: boolean = false;
  selectedTabIndex: number = 0; // 0 = Edit, 1 = Preview

  private subscriptions: Subscription[] = [];

  ngOnInit() {
    // Initialize with existing snapshot data
    this.snapshotName = this.data.snapshot.name;
    this.description = this.data.snapshot.content || '';

    // Initialize markdown service subscription
    this.initMarkdownServiceSubscription();

    // Render initial markdown if description exists
    if (this.description.trim()) {
      this.renderMarkdown();
    }

    // Focus the name input after the modal opens
    setTimeout(() => {
      this.nameInput?.nativeElement.focus();
    }, 100);
  }

  ngOnDestroy() {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  private initMarkdownServiceSubscription() {
    const subscription = this.markdownService.processedDataSubject$.subscribe({
      next: (data: any) => {
        // Use snapshot ID as chunk ID for markdown processing
        if (String(data.stepId) === String(this.data.snapshot.snapshotId)) {
          if (data.chunkValueName === 'content') {
            this.renderedMarkdown.set(data.markdown);
          }
        }
      },
      error: (error) => {
        console.error('Error processing markdown:', error);
      },
    });
    this.subscriptions.push(subscription);
  }

  private renderMarkdown() {
    if (this.description.trim()) {
      // Use snapshot ID as unique identifier for markdown processing
      this.markdownService.getMarkdown(
        this.description,
        String(this.data.snapshot.snapshotId),
        true, // ignore cache for real-time editing
        'content',
      );
    } else {
      this.renderedMarkdown.set('');
    }
  }

  onDescriptionChange() {
    // Re-render markdown when description changes
    this.renderMarkdown();
  }

  onTabChange(index: number) {
    this.selectedTabIndex = index;
    this.isPreviewMode = index === 1;

    // Render markdown when switching to preview tab
    if (this.isPreviewMode) {
      this.renderMarkdown();
    }
  }

  onNameInput() {
    // Remove any leading/trailing spaces and limit length
    this.snapshotName = this.snapshotName.trim();
  }

  onCancel() {
    this.dialogRef.close({
      action: 'cancel',
    } as EditSnapshotModalResult);
  }

  onSaveSnapshot() {
    if (!this.snapshotName.trim()) {
      this.toastrService.error('Please enter a snapshot name');
      return;
    }

    if (this.snapshotName.length > 100) {
      this.toastrService.error(
        'Snapshot name is too long (max 100 characters)',
      );
      return;
    }

    this.isSaving.set(true);
    this.updateSnapshot(this.snapshotName.trim(), this.description.trim());
  }

  private updateSnapshot(snapshotName: string, description: string) {
    const updateData = {
      name: snapshotName,
      content: description,
    };

    this.snapshotService
      .updateSnapshot(this.data.snapshot.snapshotId, updateData)
      .subscribe({
        next: () => {
          this.toastrService.success('Snapshot updated successfully');
          this.dialogRef.close({
            action: 'save',
            snapshotName: snapshotName,
            description: description,
          } as EditSnapshotModalResult);
        },
        error: (error) => {
          console.error('Error updating snapshot:', error);
          this.toastrService.error('Failed to update snapshot');
          this.isSaving.set(false);
        },
      });
  }

  get isValid(): boolean {
    return (
      this.snapshotName.trim().length > 0 && this.snapshotName.length <= 100
    );
  }

  get remainingChars(): number {
    return 100 - this.snapshotName.length;
  }

  get hasChanges(): boolean {
    return (
      this.snapshotName.trim() !== this.data.snapshot.name ||
      this.description.trim() !== (this.data.snapshot.content || '')
    );
  }
}
